import type { CSSProperties } from "react";
import { defaultArrowOptions, type ChessboardOptions } from "react-chessboard";
import type { ChessArrow, ChessSquare, ChessSquareStyles } from "./types";
import { isChessSquare } from "./types";

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

export interface ChesslaBoardOptions {
  id: string;
  position: string;
  orientation?: "white" | "black";
  animationDuration?: number;
  allowDragging?: boolean;
  allowDrawingArrows?: boolean;
  arrows?: ChessArrow[];
  squareStyles?: ChessSquareStyles;
  lightSquareStyle?: CSSProperties;
  darkSquareStyle?: CSSProperties;
  boardStyle?: CSSProperties;
  onPieceDrop?: (source: ChessSquare, target: ChessSquare) => boolean;
  onSquareClick?: (square: ChessSquare) => void;
  onArrowsChange?: (arrows: ChessArrow[]) => void;
}

export const buildChessboardOptions = (input: ChesslaBoardOptions): ChessboardOptions => ({
  id: input.id,
  position: input.position === "start" ? START_FEN : input.position,
  boardOrientation: input.orientation ?? "white",
  animationDurationInMs: input.animationDuration ?? 0,
  showAnimations: (input.animationDuration ?? 0) > 0,
  allowDragging: input.allowDragging ?? input.onPieceDrop != null,
  allowDragOffBoard: false,
  allowAutoScroll: false,
  dragActivationDistance: 3,
  allowDrawingArrows: input.allowDrawingArrows ?? true,
  clearArrowsOnClick: true,
  clearArrowsOnPositionChange: true,
  arrowOptions: { ...defaultArrowOptions, opacity: 0.82, activeOpacity: 0.72 },
  // The board keys SVG arrows by endpoints, not color. Duplicate keys leave
  // orphaned arrows behind when a later position removes those endpoints.
  arrows: input.arrows?.filter(
    (arrow, index, all) =>
      all.findIndex(
        (candidate) => candidate.startSquare === arrow.startSquare && candidate.endSquare === arrow.endSquare,
      ) === index,
  ),
  squareStyles: input.squareStyles,
  lightSquareStyle: input.lightSquareStyle,
  darkSquareStyle: input.darkSquareStyle,
  boardStyle: { borderRadius: 0, ...input.boardStyle },
  onPieceDrop: input.onPieceDrop
    ? ({ sourceSquare, targetSquare }) =>
        isChessSquare(sourceSquare) && isChessSquare(targetSquare)
          ? input.onPieceDrop!(sourceSquare, targetSquare)
          : false
    : undefined,
  onSquareClick: input.onSquareClick
    ? ({ square }) => {
        if (isChessSquare(square)) input.onSquareClick!(square);
      }
    : undefined,
  onArrowsChange: input.onArrowsChange
    ? ({ arrows }) =>
        input.onArrowsChange!(
          arrows.flatMap((arrow) =>
            isChessSquare(arrow.startSquare) && isChessSquare(arrow.endSquare)
              ? [{ ...arrow, startSquare: arrow.startSquare, endSquare: arrow.endSquare }]
              : [],
          ),
        )
    : undefined,
});
